import React from "react";
import NewOrderLine from "./newOrderLine";
import OrderLine from "./orderLine";
import { iField, Material } from "/app/src/models";
import { useTranslation } from "react-i18next";
import DisabledWrapper from "/app/src/components/generic/components/disabledWrapper";

interface OrderLineType {
  id?: number;
  materialName: string;
  quantity: number;
  allocate?: string;
  costCenterId?: string;
  hostIdentification?: string;
  releaseDate?: string;
  qualification?: string;
  productionDate?: string;
  expiryDate?: string;
  serialNumber?: string;
  Info1?: string;
  Info2?: string;
  Info3?: string;
  Info4?: string;
  Info5?: string;
}

/**
 * Renders the current order with its lines and allows modifying the order.
 *
 * @param {Object} props - The properties for the current order.
 * @param {Line[]} props.lines - An array of order lines.
 * @param {(line: Line) => void} props.addLine - A function to add a new order line.
 * @param {(lineId: number) => void} props.removeLine - A function to remove an existing order line.
 * @param {(lineId: number, newQuantity: number) => void} props.updateLineQuantity - A function to update the quantity of an order line.
 * @returns {JSX.Element} - The rendered order component.
 */
export default function CurrentOrder({
  lines,
  addLine,
  removeLine,
  orderLineFields,
  updateLine,
  disabled,
  directionType,
  selectedMaterial,
}: {
  lines: OrderLineType[];
  addLine: (line: OrderLineType) => void;
  removeLine: (lineId: number) => void;
  orderLineFields: iField[];
  updateLine: (lineId: number, newLine: OrderLineType) => void;
  disabled: boolean;
  directionType?: string;
  selectedMaterial?: Material | null;
}) {
  const { t } = useTranslation();
  return (
    <DisabledWrapper
      tooltip={t("translation:order_lines_tip")}
      disabled={disabled}
    >
      {lines.map((line) => (
        <OrderLine
          key={line.id}
          line={line}
          removeLine={removeLine}
          orderLineFields={orderLineFields}
          updateLine={updateLine}
          directionType={directionType}
        />
      ))}
      <NewOrderLine
        addLine={addLine}
        orderLineFields={orderLineFields}
        directionType={directionType}
        selectedMaterial={selectedMaterial}
      />
    </DisabledWrapper>
  );
}
